import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOverview } from '../../api/dashboard';
import { useAuth } from '../../auth/AuthContext';
import PageHeader from '../../components/PageHeader';
import { formatShift } from '../../utils/format';
import './Admin.css';

const show = (value) => (value === null || value === undefined ? '—' : value);

function StatCard({ value, label, sub, onClick }) {
  return (
    <button type="button" className="stat-card" onClick={onClick}>
      <span className="stat-value">{show(value)}</span>
      <span className="stat-label">{label}</span>
      {sub && <span className="stat-sub">{sub}</span>}
    </button>
  );
}

function QuickAction({ icon, title, desc, onClick }) {
  return (
    <button type="button" className="quick-action-card" onClick={onClick}>
      <span className="quick-action-icon" aria-hidden="true">{icon}</span>
      <span className="quick-action-text">
        <span className="quick-action-title">{title}</span>
        <span className="quick-action-desc">{desc}</span>
      </span>
    </button>
  );
}

// Admins see aggregate counts for the organization, never report sheets.
// Charge nurses see their own unit's report sheets and nurses. All the counts
// come from one request; until it answers (or if it fails) they show "—".
const Overview = () => {
  const navigate = useNavigate();
  const { nurse: user } = useAuth();
  const isAdmin = user.role === 'admin';
  const unitName = user.unit?.name || 'Your unit';
  const [stats, setStats] = useState({});

  useEffect(() => {
    let cancelled = false;
    getOverview()
      .then((data) => { if (!cancelled) setStats(data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <PageHeader title={isAdmin ? 'Admin Overview' : `${unitName} Overview`} />
      <div className="component-container">
        <div className="overview-content">
          {isAdmin ? (
            <div className="stats-row">
              <StatCard value={stats.unitCount} label="Units" onClick={() => navigate('/units')} />
              <StatCard value={stats.nurseCount} label="Nurse Accounts" onClick={() => navigate('/nurses')}
                sub={`${show(stats.neverLoggedInCount)} never logged in`} />
              <StatCard value={stats.currentShift?.handoffCount} label="Handoffs This Shift" onClick={() => navigate('/insights')}
                sub={stats.currentShift ? formatShift(stats.currentShift.date, stats.currentShift.shift) : '—'} />
            </div>
          ) : (
            <div className="stats-row">
              <StatCard value={stats.reportSheetCount} label="Current Report Sheets" onClick={() => navigate('/report-sheets')} />
              <StatCard value={stats.nurseCount} label="Nurses" onClick={() => navigate('/nurses')}
                sub={`${show(stats.neverLoggedInCount)} never logged in`} />
            </div>
          )}

          <section className="overview-section">
            <h2 className="section-title">Quick Actions</h2>
            <div className="quick-actions-grid">
              {isAdmin ? (
                <>
                  <QuickAction icon="📊" title="View Insights" onClick={() => navigate('/insights')}
                    desc="See which checklist items nurses mentioned or missed this shift" />
                  <QuickAction icon="🏥" title="View Units" onClick={() => navigate('/units')}
                    desc="See each unit's nurses and rooms" />
                  <QuickAction icon="🩺" title="Manage Nurses" onClick={() => navigate('/nurses')}
                    desc="Assign nurses to units and see when they last logged in" />
                </>
              ) : (
                <>
                  <QuickAction icon="📋" title="Browse Report Sheets" onClick={() => navigate('/report-sheets')}
                    desc={`See every current report sheet on ${unitName}`} />
                  <QuickAction icon="🩺" title="View Nurses" onClick={() => navigate('/nurses')}
                    desc={`See the nurses on ${unitName} and when they last logged in`} />
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Overview;
